import styles from './IconButton.module.css';

import React from 'react';
import Tooltip from '../Tooltip';
import Icon from '../../helpers/components/Icon';
import classNames from 'classnames';

const COMPONENT_NAME = 'IconButton'; // Название компонента для использования в логах

const COLOR_MAP = {
  primary: 'var(--color-brand)',
  accent: 'var(--color-accent)',
  error: 'var(--color-error)',
  secondary: 'var(--color-secondary)',
};

const ALLOWED_VARIANTS = ['default', 'outlined', 'flat'];

/**
 * **IconButton** — это компонент кнопки с иконкой, поддерживающий различные стили, цвета и подсказки.
 * Он предоставляет гибкость в настройке внешнего вида и поведения,
 * а также включает проверки на корректность переданных параметров.
 *
 * ### Описание параметров:
 *
 * @param {string} tooltip - Текст всплывающей подсказки, отображаемой при наведении.
 * @param {{}} icon - Путь или ссылка на изображение иконки (src), отображаемой в кнопке.
 * @param {'primary'|'accent'|'error'|'secondary'} [color='primary'] - Цвет кнопки.
 *    Допустимые значения:
 *    - `'primary'` — основной цвет.
 *    - `'accent'` — акцентный цвет.
 *    - `'error'` — цвет ошибки.
 *    - `'secondary'` — системный доп. цвет.
 * @param {'default'|'outlined'|'flat'} [variant='default'] - Вариация стиля кнопки.
 *    Допустимые значения:
 *    - `'default'` — стандартная кнопка.
 *    - `'outlined'` — кнопка с контуром.
 *    - `'flat'` — "плоская" кнопка (с фоном).
 * @param {number} [size=16] - Размер иконки в пикселях (ширина и высота иконки).
 * @param {function} onClick - Обработчик клика (функция, вызываемая при клике на кнопку).
 * @param {boolean} [disabled=false] - Состояние блокировки кнопки.
 *
 *
 * ### Пример использования:
 * ```jsx
 * import IconButton from './IconButton';
 * import editIcon from 'assets/svg/edit-01.svg';
 *
 * <IconButton
 *   tooltip={'Переименовать'}
 *   icon={editIcon}
 *   variant="outlined"
 *   onClick={() => console.log('Клик')}
 * />
 * ```
 */
const IconButton = ({
  tooltip,
  icon,
  color = 'primary',
  variant = 'default',
  size = 16,
  onClick,
  disabled = false,
}) => {
  let resolvedColor = COLOR_MAP[color];

  // Проверка и логирование для пропса color
  if (!resolvedColor) {
    console.warn(
      `[${COMPONENT_NAME}] Invalid value for prop "color": "${color}". Defaulting to "primary".`,
    );
    resolvedColor = COLOR_MAP.primary;
  }

  // Проверка и логирование для пропса variant
  if (!ALLOWED_VARIANTS.includes(variant)) {
    console.warn(
      `[${COMPONENT_NAME}] Invalid value for prop "variant": "${variant}". Defaulting to "default".`,
    );
    variant = 'default';
  }

  return (
    <Tooltip text={tooltip}>
      <button
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          styles.iconButton,
          styles[`color-${color}`], // Динамические классы для цвета
          styles[`variant-${variant}`], // Динамические классы для варианта
          {
            [styles.disabled]: disabled,
          },
        )}
      >
        <Icon
          className={styles.icon}
          src={icon}
          color={variant === 'flat' ? 'white' : resolvedColor} // Белый цвет для flat
          width={variant === 'default' && size === 16 ? `24px` : `${size}px`}
          height={variant === 'default' && size === 16 ? `24px` : `${size}px`}
        />
      </button>
    </Tooltip>
  );
};

export default IconButton;
